import { I18n } from '@calendly/i18n';
import { BareButton } from '@calendly/ui/components/bare-button';
import { CloseIcon } from '@calendly/ui/components/icons';
import { Loader } from '@calendly/ui/components/loader';
import { Tab, Tabs } from '@calendly/ui/components/tabs';
import classNames from 'classnames';
import { forwardRef, useContext } from 'react';
import { MeetingDetailHeader, NotetakerProvider, ReportEventModal, DetailsTab, ExternalMeetingDetails, NotesTab } from './components';
import { MeetingDetailDependenciesContext } from './meeting_detail_context';
const globalDetailsContainerCss = "geilv8r";
const GlobalDetailsContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} data-testid="meeting-details" className={classNames(globalDetailsContainerCss, props.className)} />;
});
const closeButtonCss = "ckmvbgc";
const CloseButton = forwardRef((props, ref) => <BareButton ref={ref} {...props} aria-label={I18n.t('publisher.action.close')} className={classNames(closeButtonCss, props.className)}>
    <CloseIcon />
  </BareButton>);
const headerContainerCss = "h1jd7ewc";
const HeaderContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(headerContainerCss, props.className)} />;
});
const headerLoaderContainerCss = "h1hiprxw";
const HeaderLoaderContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(headerLoaderContainerCss, props.className)} />;
});
const contentLoaderContainerCss = "c16le60c";
const ContentLoaderContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(contentLoaderContainerCss, props.className)} />;
});

/**
 * Presentational component for MeetingDetail.
 * This component is pure and receives all data as props, making it easy to test
 * and use in Storybook without needing data fetching, Redux, or context.
 *
 * @param {Object} props
 * @param {Object} props.selectedEvent - The selected meeting event
 * @param {boolean} props.isLoading - Whether the component is in a loading state
 * @param {string} props.tab - The currently selected tab
 * @param {Array} props.todayMeetings - Array of meetings for the same day
 * @param {Object} props.currentUser - Current user data
 * @param {Object} props.miSettings - Meeting intelligence settings
 * @param {boolean} props.isPending - Whether the event is pending
 * @param {boolean} props.isCalMeeting - Whether this is a Calendly meeting
 * @param {boolean} props.showReportModal - Whether to show the report modal
 * @param {string} props.tempTab - Temporary tab for leave page modal
 * @param {Function} props.setTempTab - Handler for setting the temporary tab
 * @param {boolean} props.showLeavePageModal - Whether to show leave page modal
 * @param {Function} props.onClose - Handler for closing the detail view
 * @param {Function} props.onTabClick - Handler for tab clicks
 * @param {Function} props.onReportModalOpen - Handler for opening report modal
 * @param {Function} props.onReportModalClose - Handler for closing report modal (on success)
 * @param {Function} props.onReportModalCancel - Handler for canceling report modal (nevermind)
 * @param {Function} props.onNotesTabClick - Handler for notes tab click
 * @param {Function} props.onSwitchToTab - Handler for switching tabs
 * @param {Function} props.onCloseLeavePageModal - Handler for closing leave page modal
 * @param {Function} props.updateSelectedEvent - Handler for updating the selected event (e.g., after cancellation)
 */
export const MeetingDetailView = ({
  selectedEvent,
  isLoading,
  tab,
  todayMeetings,
  currentUser,
  miSettings,
  isPending,
  isCalMeeting,
  showReportModal,
  tempTab,
  setTempTab,
  showLeavePageModal,
  onClose,
  onTabClick,
  onReportModalOpen,
  onReportModalClose,
  onReportModalCancel,
  onNotesTabClick,
  onSwitchToTab,
  onCloseLeavePageModal,
  updateSelectedEvent
}) => {
  const validTabs = ['details', 'notes'];
  const normalizedTab = validTabs.includes(tab) ? tab : undefined;

  // Get dependencies from context - these are provided by the container
  const dependencies = useContext(MeetingDetailDependenciesContext);
  if (!dependencies) {
    throw new Error('MeetingDetailView must be wrapped in a MeetingDetailDependenciesProvider');
  }

  // Use NotetakerProvider from context when available (e.g. full app), otherwise fall back to imported one for Storybook/minimal context
  const NotetakerWrapper = dependencies.NotetakerProvider || NotetakerProvider;
  const wrapInNotetaker = content => NotetakerWrapper ? <NotetakerWrapper event={selectedEvent}>{content}</NotetakerWrapper> : content;
  if (!selectedEvent && !isLoading) {
    return null;
  }

  // Show full loading only when we have no data yet. Once we have selectedEvent (e.g. from cache),
  // keep showing header + content so the view doesn't jump when REST/merge data arrives.
  const showFullLoading = isLoading && !selectedEvent;
  return <GlobalDetailsContainer>
      <CloseButton onClick={onClose} />
      <HeaderContainer>
        {showFullLoading ? <HeaderLoaderContainer>
            <Loader size="medium" />
          </HeaderLoaderContainer> : <>
            <MeetingDetailHeader selectedEvent={selectedEvent} todayMeetings={todayMeetings} showReportModal={onReportModalOpen} clearSelectedEvent={onClose} updateSelectedEvent={updateSelectedEvent} />
            {!isPending && isCalMeeting && <Tabs>
                <Tab onClick={() => onTabClick('details')} selected={normalizedTab === 'details' || !normalizedTab}>
                  {I18n.t('publisher.meetings.tabs.details')}
                </Tab>
                <Tab onClick={() => onTabClick('notes')} selected={normalizedTab === 'notes'}>
                  {I18n.t('publisher.meetings.tabs.notes.header')}
                </Tab>
              </Tabs>}
          </>}
      </HeaderContainer>
      {showFullLoading ? <ContentLoaderContainer>
          <Loader size="large" />
        </ContentLoaderContainer> : <>
          {(normalizedTab === 'details' || isCalMeeting && !normalizedTab || isPending) && <DetailsTab selectedEvent={selectedEvent} />}
          {isCalMeeting && !isPending && normalizedTab === 'notes' && wrapInNotetaker(<NotesTab selectedEvent={selectedEvent} setTempTab={setTempTab} tempTab={tempTab} closeLeavePageModal={onCloseLeavePageModal} showLeavePageModal={showLeavePageModal} switchToTab={onSwitchToTab} miSettings={miSettings} user={currentUser} />)}
          {!isCalMeeting && !isPending && wrapInNotetaker(<ExternalMeetingDetails meeting={selectedEvent} miSettings={miSettings} user={currentUser} />)}
          {dependencies.RightRailPaywall && <dependencies.RightRailPaywall />}
        </>}
      {showReportModal && ReportEventModal && <ReportEventModal event={selectedEvent} onClose={onReportModalClose} onCancel={onReportModalCancel} />}
    </GlobalDetailsContainer>;
};

require("./meeting_detail_view.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./meeting_detail_view.js");